import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTheme } from '../context/ThemeContext'
import { apiConfig } from '../auth/authConfig'
import KPICard from '../components/KPICard'
import PLStatement from '../components/PLStatement'
import {
    ComposedChart, BarChart, Bar, Line, XAxis, YAxis, CartesianGrid,
    Tooltip, Legend, ResponsiveContainer
} from 'recharts'

const OIL_PRICES = [55, 60, 65, 70, 80]
const DEDUCT_PER_BBL = 2.50

function formatDollars(val) {
    if (val == null) return '$0'
    const abs = Math.abs(val)
    if (abs >= 1_000_000) return `$${(val / 1_000_000).toFixed(1)}M`
    if (abs >= 1_000) return `$${(val / 1_000).toFixed(0)}K`
    return `$${val.toLocaleString()}`
}

function FinancialsOverview() {
    const { theme } = useTheme()
    const navigate = useNavigate()
    const [data, setData] = useState(null)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState(null)
    const [selectedYear, setSelectedYear] = useState(null) // null = rolling
    const [oilPrice, setOilPrice] = useState(null) // null = no projection
    const [viewMode, setViewMode] = useState('gross') // 'gross' or 'net'

    const fetchData = useCallback(async (year) => {
        setLoading(true)
        setError(null)
        try {
            const url = year
                ? `${apiConfig.baseUrl}/financials?year=${year}`
                : `${apiConfig.baseUrl}/financials`
            const res = await fetch(url)
            if (!res.ok) throw new Error('Failed to fetch financial data')
            const json = await res.json()
            setData(json)
        } catch (err) {
            setError(err.message)
        } finally {
            setLoading(false)
        }
    }, [])

    useEffect(() => {
        fetchData(selectedYear)
    }, [selectedYear, fetchData])

    const handleYearChange = (year) => {
        setSelectedYear(year === selectedYear ? null : year)
    }

    if (loading && !data) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-aviator-600"></div>
            </div>
        )
    }

    if (error && !data) {
        return (
            <div className="bg-red-900/20 border border-red-800 rounded-lg p-4 text-red-400">
                <p className="font-semibold">Error loading financial data</p>
                <p className="text-sm mt-1">{error}</p>
                <button onClick={() => fetchData(selectedYear)}
                    className="mt-3 text-sm bg-red-800 hover:bg-red-700 text-white px-3 py-1 rounded transition">
                    Retry
                </button>
            </div>
        )
    }

    const summary = data?.annualSummary || {}
    const isRolling = data?.isRolling
    const periodLabel = data?.periodLabel || data?.selectedYear || ''

    // Build monthly with chart labels and optional projection
    const isNet = viewMode === 'net'
    const forecastBBL = isNet ? (data?.projectionNetBBL || 0) : (data?.projectionBBL || 0)
    const monthly = (data?.monthly || []).map(m => {
        const row = { ...m, name: m.shortLabel || m.month }
        if (oilPrice) {
            // Use actual BBL for months with production, forecast avg for future months
            const bbl = isNet ? (m.netOilBBL || 0) : (m.oilBBL || 0)
            const monthBBL = bbl > 0 ? bbl : forecastBBL
            if (monthBBL > 0) {
                row.projected = Math.round(monthBBL * (oilPrice - DEDUCT_PER_BBL))
            }
        }
        return row
    })

    const wti = data?.wti || {}
    const hasTargets = summary.totalGrossTarget > 0
    const isDark = theme === 'dark'

    const yearParam = selectedYear ? `?year=${selectedYear}` : ''

    const handleRevenueBarClick = (barData) => {
        if (barData?.activePayload?.[0]?.payload) {
            navigate(`/financials/revenue${yearParam}`)
        }
    }

    // Navigate to month detail on LOE chart click
    const handleLOEBarClick = (barData) => {
        if (!barData?.activePayload?.length) return
        const payload = barData.activePayload[0]?.payload
        if (payload?.year && payload?.monthNum) {
            const pad = String(payload.monthNum).padStart(2, '0')
            navigate(`/financials/loe/month/${payload.year}-${pad}`)
        }
    }

    const chartColors = {
        revenueActual: '#22c55e',
        revenueTarget: '#3b82f6',
        projected: '#facc15',
        loeActual: '#6366f1',
        loePerBBL: '#f97316',
        grid: isDark ? '#334155' : '#e2e8f0',
        text: isDark ? '#94a3b8' : '#64748b',
        tooltipBg: isDark ? '#1e293b' : '#ffffff',
        tooltipBorder: isDark ? '#334155' : '#e2e8f0'
    }

    const CustomTooltip = ({ active, payload, label }) => {
        if (!active || !payload?.length) return null
        return (
            <div style={{
                background: chartColors.tooltipBg,
                border: `1px solid ${chartColors.tooltipBorder}`,
                borderRadius: 8, padding: '10px 14px', fontSize: 13
            }}>
                <p style={{ fontWeight: 600, marginBottom: 4, color: isDark ? '#fff' : '#111' }}>{label}</p>
                {payload.map((entry, i) => (
                    <p key={i} style={{ color: entry.color, margin: '2px 0' }}>
                        {entry.name}: {entry.name === 'LOE/BBL'
                            ? `$${entry.value?.toFixed(2)}`
                            : `$${Math.round(entry.value).toLocaleString()}`}
                    </p>
                ))}
            </div>
        )
    }

    return (
        <div className="space-y-6">
            {/* Header + Year Toggle */}
            <div className={`border-b-2 ${isDark ? 'border-blue-800' : 'border-blue-200'} pb-4`}>
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <div>
                        <h1 className={`text-xl sm:text-3xl font-bold ${isDark ? 'text-white' : 'text-gray-900'} flex items-center gap-2 sm:gap-3`}>
                            <span className="inline-block w-1 h-6 sm:h-8 bg-blue-500 flex-shrink-0"></span>
                            Financials
                        </h1>
                        <p className={`${isDark ? 'text-blue-300' : 'text-blue-700'} text-xs sm:text-sm mt-1 ml-3 sm:ml-4`}>
                            {isRolling ? `Rolling 13 months \u2022 Data through ${data?.latestCompleteMonth}` : `${periodLabel} \u2022 Data through ${data?.latestCompleteMonth}`}
                            {data?.lastETLUpdate && (
                                <span className={`ml-2 ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                                    {'\u2022'} WolfPak sync: {new Date(data.lastETLUpdate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} {new Date(data.lastETLUpdate).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                                </span>
                            )}
                            {loading && <span className="ml-2 text-xs opacity-60">Updating...</span>}
                        </p>
                    </div>

                    <div className="flex items-center gap-3 ml-3 sm:ml-0">
                        {/* Gross/Net Toggle */}
                        <div className={`inline-flex rounded-lg p-0.5 ${isDark ? 'bg-slate-800 border border-slate-700' : 'bg-gray-200 border border-gray-300'}`}>
                            <button onClick={() => setViewMode('gross')}
                                className={`px-3 sm:px-4 py-1.5 text-xs sm:text-sm font-medium rounded-md transition-all ${
                                    viewMode === 'gross' ? 'bg-blue-600 text-white shadow-sm'
                                        : isDark ? 'text-gray-400 hover:text-gray-200' : 'text-gray-600 hover:text-gray-900'
                                }`}>Gross</button>
                            <button onClick={() => setViewMode('net')}
                                className={`px-3 sm:px-4 py-1.5 text-xs sm:text-sm font-medium rounded-md transition-all ${
                                    viewMode === 'net' ? 'bg-blue-600 text-white shadow-sm'
                                        : isDark ? 'text-gray-400 hover:text-gray-200' : 'text-gray-600 hover:text-gray-900'
                                }`}>Net (NRI)</button>
                        </div>
                        {/* Year Toggle Pills */}
                        <div className="flex gap-1">
                            <button onClick={() => setSelectedYear(null)}
                                className={`px-3 py-1.5 text-sm font-medium rounded-full transition-all ${
                                    selectedYear === null
                                        ? 'bg-blue-600 text-white shadow-lg'
                                        : isDark
                                            ? 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                                            : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                                }`}>
                                13 Mo
                            </button>
                            {(data?.availableYears || []).map(yr => (
                                <button key={yr} onClick={() => handleYearChange(yr)}
                                    className={`px-4 py-1.5 text-sm font-medium rounded-full transition-all ${
                                        yr === selectedYear
                                            ? 'bg-blue-600 text-white shadow-lg'
                                            : isDark
                                                ? 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                                                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                                    }`}>
                                    {yr}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            {/* Summary KPI Cards */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
                <KPICard
                    title={isNet ? "NRI Revenue" : "Gross Revenue"}
                    value={`$${Math.round(isNet ? (summary.totalNriRevenue || 0) : (summary.totalGrossRevenue || 0)).toLocaleString()}`}
                    onClick={() => navigate(`/financials/revenue${yearParam}`)}
                />
                <KPICard
                    title="Net Revenue"
                    value={`$${Math.round(summary.totalNetRevenue || 0).toLocaleString()}`}
                    onClick={() => navigate(`/financials/revenue${yearParam}`)}
                />
                <KPICard
                    title={isNet ? "LOE (WI)" : "Total LOE"}
                    value={`$${Math.round(isNet ? (summary.totalWiLOE || 0) : (summary.totalLOE || 0)).toLocaleString()}`}
                    onClick={() => navigate(`/financials/loe${yearParam}`)}
                />
                <KPICard
                    title="WTI Price"
                    value={wti.current ? `$${wti.current.toFixed(2)}` : '$--'}
                    unit="/BBL"
                    onClick={() => navigate(`/financials/revenue${yearParam}`)}
                />
            </div>

            {/* Revenue Chart with Oil Price Projections */}
            <div className="chart-container">
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2 mb-4">
                    <h2 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                        Monthly {isNet ? 'NRI' : 'Gross'} Revenue{hasTargets ? ': Target vs Actual' : ''}
                    </h2>
                    <div className="flex items-center gap-3">
                        {/* Oil Price Toggle */}
                        <div className="flex items-center gap-1">
                            <span className={`text-xs font-medium mr-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                                Project @
                            </span>
                            {OIL_PRICES.map(p => (
                                <button key={p} onClick={() => setOilPrice(oilPrice === p ? null : p)}
                                    className={`px-2 py-0.5 text-xs font-medium rounded-full transition-all ${
                                        oilPrice === p
                                            ? 'bg-yellow-500 text-black shadow'
                                            : isDark
                                                ? 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                                                : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                                    }`}>
                                    ${p}
                                </button>
                            ))}
                        </div>
                        <button onClick={() => navigate(`/financials/revenue${yearParam}`)}
                            className={`text-sm ${isDark ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-800'}`}>
                            View Details &rarr;
                        </button>
                    </div>
                </div>
                <ResponsiveContainer width="100%" height={300}>
                    <ComposedChart data={monthly} margin={{ top: 5, right: 10, left: 10, bottom: 5 }}
                        onClick={handleRevenueBarClick} style={{ cursor: 'pointer' }}>
                        <CartesianGrid strokeDasharray="3 3" stroke={chartColors.grid} />
                        <XAxis dataKey="name" tick={{ fill: chartColors.text, fontSize: 11 }} />
                        <YAxis tickFormatter={formatDollars} tick={{ fill: chartColors.text, fontSize: 12 }} width={65} />
                        <Tooltip content={<CustomTooltip />} />
                        <Legend wrapperStyle={{ fontSize: 13, color: chartColors.text }} />
                        {hasTargets && (
                            <Bar dataKey={isNet ? 'netTarget' : 'grossTarget'} name="Target" fill={chartColors.revenueTarget} radius={[2, 2, 0, 0]} opacity={0.5} />
                        )}
                        <Bar dataKey={isNet ? 'nriRevenue' : 'grossRevenue'} name="Actual" fill={chartColors.revenueActual} radius={[2, 2, 0, 0]} />
                        {oilPrice && (
                            <Line type="monotone" dataKey="projected" name={`Projected @$${oilPrice}`}
                                stroke={chartColors.projected} strokeWidth={2} dot={{ r: 3, fill: chartColors.projected }}
                                connectNulls={false} />
                        )}
                    </ComposedChart>
                </ResponsiveContainer>
                {oilPrice && (
                    <p className={`text-xs mt-2 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                        Projected = Actual BBL/mo x (${oilPrice} - ${DEDUCT_PER_BBL.toFixed(2)} deduct/bbl) &bull; Future months use {forecastBBL.toLocaleString()} BBL avg
                    </p>
                )}
            </div>

            {/* LOE Bar Chart + LOE/BBL Line */}
            <div className="chart-container">
                <div className="flex justify-between items-center mb-4">
                    <h2 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                        Monthly {isNet ? 'LOE (WI)' : 'LOE'} & Lifting Cost
                        <span className={`text-xs font-normal ml-2 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                            Click a month to drill down
                        </span>
                    </h2>
                    <button onClick={() => navigate(`/financials/loe${yearParam}`)}
                        className={`text-sm ${isDark ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-800'}`}>
                        View Details &rarr;
                    </button>
                </div>
                <ResponsiveContainer width="100%" height={300}>
                    <ComposedChart data={monthly} margin={{ top: 5, right: 10, left: 10, bottom: 5 }}
                        onClick={handleLOEBarClick} style={{ cursor: 'pointer' }}>
                        <CartesianGrid strokeDasharray="3 3" stroke={chartColors.grid} />
                        <XAxis dataKey="name" tick={{ fill: chartColors.text, fontSize: 11 }} />
                        <YAxis yAxisId="left" tickFormatter={formatDollars} tick={{ fill: chartColors.text, fontSize: 12 }} width={65} />
                        <YAxis yAxisId="right" orientation="right"
                            tickFormatter={v => `$${v}`}
                            tick={{ fill: chartColors.loePerBBL, fontSize: 12 }} width={55} />
                        <Tooltip content={<CustomTooltip />} />
                        <Legend wrapperStyle={{ fontSize: 13, color: chartColors.text }} />
                        <Bar yAxisId="left" dataKey={isNet ? 'wiLoeActual' : 'loeActual'} name="LOE" fill={chartColors.loeActual} radius={[2, 2, 0, 0]} cursor="pointer" />
                        <Line yAxisId="right" type="monotone" dataKey={isNet ? 'nriLoePerBBL' : 'loePerBBL'} name="LOE/BBL"
                            stroke={chartColors.loePerBBL} strokeWidth={2}
                            dot={{ r: 3, fill: chartColors.loePerBBL }}
                            connectNulls={false} />
                    </ComposedChart>
                </ResponsiveContainer>
            </div>

            {/* Profit & Loss Statement */}
            {monthly.length > 0 && (
                <div className="chart-container">
                    <h2 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'} mb-4`}>
                        Profit & Loss Statement
                        <span className={`text-xs font-normal ml-2 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                            Click values to drill down
                        </span>
                    </h2>
                    <PLStatement
                        monthly={monthly}
                        isDark={isDark}
                        viewMode={viewMode}
                        loeCategoryNames={data?.loeCategoryNames}
                        onCellClick={(row, m) => {
                            const pad = String(m.monthNum).padStart(2, '0')
                            const cat = row.category ? `&category=${encodeURIComponent(row.category)}` : ''
                            navigate(`/financials/loe/month/${m.year}-${pad}?type=${row.clickType}${cat}`)
                        }}
                    />
                </div>
            )}
        </div>
    )
}

export default FinancialsOverview
